import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '../../../StockControl';
import { ARGENTINA_PROVINCES } from '../../logistics/components/LogisticsUtils';
import { computePurchaseHeaderStatus, computePurchaseLineStatus } from '../stockFlowUtils';
import { stockButtonTones } from '../stockUi';

const PURCHASE_STATUS_STYLES = {
  draft: 'bg-slate-200 text-slate-700',
  pending_pickup: 'bg-amber-100 text-amber-700',
  partially_assigned: 'bg-violet-100 text-violet-700',
  assigned_to_route: 'bg-cyan-100 text-cyan-700',
  in_transit: 'bg-sky-100 text-sky-700',
  partially_received: 'bg-emerald-100 text-emerald-700',
  received: 'bg-emerald-200 text-emerald-800',
  cancelled: 'bg-rose-100 text-rose-700',
};

const PURCHASE_STATUS_LABELS = {
  draft: 'Borrador',
  pending_pickup: 'Pendiente de retiro',
  partially_assigned: 'Parcialmente asignada',
  assigned_to_route: 'Asignada a ruta',
  in_transit: 'En tránsito',
  partially_received: 'Recepción parcial',
  received: 'Recibida',
  cancelled: 'Cancelada',
};



const PURCHASE_LINE_STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  pending_pickup: 'bg-amber-100 text-amber-700',
  partially_assigned: 'bg-violet-100 text-violet-700',
  assigned_to_route: 'bg-cyan-100 text-cyan-700',
  inbound_in_transit: 'bg-sky-100 text-sky-700',
  in_transit: 'bg-sky-100 text-sky-700',
  partially_received: 'bg-emerald-100 text-emerald-700',
  received: 'bg-emerald-200 text-emerald-800',
  stored: 'bg-emerald-200 text-emerald-800',
  repair: 'bg-violet-100 text-violet-700',
  returned: 'bg-amber-100 text-amber-700',
  damaged: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-rose-100 text-rose-700',
};

const PURCHASE_LINE_STATUS_LABELS = {
  draft: 'Borrador',
  pending_pickup: 'Pendiente de retiro',
  partially_assigned: 'Parcialmente asignado',
  assigned_to_route: 'Asignado a ruta',
  inbound_in_transit: 'Tránsito de ingreso',
  in_transit: 'Tránsito de ingreso',
  partially_received: 'Recepción parcial',
  received: 'Recibido',
  stored: 'Guardado en depósito',
  repair: 'En reparación',
  returned: 'Devuelto',
  damaged: 'Rotura',
  cancelled: 'Cancelado',
};

const CLOSED_PURCHASE_STATUSES = new Set(['received', 'cancelled']);
const RECENTLY_CLOSED_DAYS = 3;

const QUICK_PRODUCT_TEMPLATE = {
  name: '',
  category: '',
  model: '',
  brand: '',
  featureLabel: '',
  unitBase: 'unidad',
  sku: '',
  barcode: '',
  controlFrequency: 'manual',
  controlDay: '1',
};



const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '';
const DEFAULT_PROVINCE = 'CABA';

const toInputValue = (value) => String(value || '').trim();

const normalizeAddressValidationState = ({
  status = GEOAPIFY_API_KEY ? 'idle' : 'missing-key',
  text,
  validatedAddress = ''
} = {}) => ({
  status,
  text:
    text ||
    (GEOAPIFY_API_KEY
      ? 'Escribí la dirección y elegí una sugerencia o validá manualmente para dejar aprobado el proveedor.'
      : 'Completá la dirección y validá manualmente la ubicación para dejar el proveedor listo para logística.'),
  validatedAddress,
});

const buildSupplierAutocompleteSearchText = (values = {}) => {
  const province = toInputValue(values.supplierProvince || DEFAULT_PROVINCE);
  return [
    toInputValue(values.supplierAddress),
    toInputValue(values.supplierLocality),
    toInputValue(values.supplierPartido),
    province && province !== DEFAULT_PROVINCE ? province : '',
    'Argentina',
  ].filter(Boolean).join(', ');
};

const buildSupplierValidatedAddressText = (values = {}) => [
  toInputValue(values.supplierAddress),
  toInputValue(values.supplierApartment),
  toInputValue(values.supplierLocality),
  toInputValue(values.supplierPartido),
  toInputValue(values.supplierProvince || DEFAULT_PROVINCE),
  toInputValue(values.supplierPostalCode),
  'Argentina',
].filter(Boolean).join(', ');

const mapGeoapifySuggestionToPatch = (feature = {}) => {
  const props = feature?.properties || {};
  return {
    supplierAddress: [props.street, props.housenumber].filter(Boolean).join(' ').trim() || props.formatted || '',
    supplierApartment: '',
    supplierLocality: props.city || props.town || props.village || props.suburb || props.county || '',
    supplierPartido: props.county || props.state_district || '',
    supplierProvince: props.state || DEFAULT_PROVINCE,
    supplierPostalCode: props.postcode || '',
    supplierLatlng: feature?.geometry?.coordinates
      ? { lng: feature.geometry.coordinates[0], lat: feature.geometry.coordinates[1] }
      : null,
  };
};

const formatMoney = (value, currency = 'ARS') => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '—';
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(numeric);
  } catch {
    return `${currency} ${numeric.toLocaleString('es-AR')}`;
  }
};

const getDateMs = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const formatDateTime = (value) => {
  const dateMs = getDateMs(value);
  if (!dateMs) return 'Sin fecha visible';
  try {
    return new Intl.DateTimeFormat('es-AR', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateMs));
  } catch {
    return new Date(dateMs).toLocaleString('es-AR');
  }
};

const buildProductIdentityParts = (product = {}) =>
  [
    String(product?.category || '').trim(),
    String(product?.model || '').trim(),
    String(product?.brand || '').trim(),
    String(product?.featureLabel || '').trim(),
  ].filter(Boolean);

const buildProductIdentityText = (product = {}) => {
  const parts = buildProductIdentityParts(product);
  return parts.length ? parts.join(' · ') : 'Sin identidad ampliada';
};

const getProductFieldValue = (value, fallback = 'Sin dato') => {
  const normalized = String(value || '').trim();
  return normalized || fallback;
};

const getVisibleSystemStock = (product = {}) => {
  const buckets = product?.stockBuckets || {};
  return Math.max(0, Number(buckets?.physicalQty || 0));
};

const buildProductOptionLabel = (product = {}) => {
  const identity = buildProductIdentityText(product);
  const stock = getVisibleSystemStock(product);
  return `${product?.name || 'Producto'}${identity !== 'Sin identidad ampliada' ? ` · ${identity}` : ''} · Stock ${stock}`;
};

const StockPurchasesPanel = ({
  purchases,
  purchaseLines,
  products,
  suppliers,
  form,
  setForm,
  editingPurchaseId,
  onSupplierNameChange,
  onSupplierAddressPatch,
  onGeocodeSupplier,
  onCreate,
  onEditPurchase,
  onDeletePurchase,
  onCancelEdit,
  onQuickCreateProduct,
}) => {
  const groupedLines = useMemo(() => {
    return purchaseLines.reduce((acc, line) => {
      if (!acc[line.purchaseId]) acc[line.purchaseId] = [];
      acc[line.purchaseId].push(line);
      return acc;
    }, {});
  }, [purchaseLines]);

  const latestPurchaseInsightByProductId = useMemo(() => {
    return (purchaseLines || []).reduce((acc, line) => {
      const productId = String(line?.productId || '').trim();
      if (!productId) return acc;

      const purchase = purchases.find((item) => String(item?.id || '') === String(line?.purchaseId || '')) || null;
      const timestamp = Math.max(
        getDateMs(line?.closedAt),
        getDateMs(line?.updatedAt),
        getDateMs(line?.createdAt),
        getDateMs(purchase?.closedAt),
        getDateMs(purchase?.updatedAt),
        getDateMs(purchase?.createdAt)
      );

      const current = acc[productId];
      if (current && current.timestamp >= timestamp) return acc;

      acc[productId] = {
        timestamp,
        supplierName: line?.supplierNameSnapshot || purchase?.supplierNameSnapshot || purchase?.supplierName || '',
        unitCost: Number(line?.unitCost || 0),
        orderedQuantity: Number(line?.orderedQuantity || 0),
        purchaseStatus: computePurchaseLineStatus(line),
        dateValue:
          line?.closedAt ||
          line?.updatedAt ||
          line?.createdAt ||
          purchase?.closedAt ||
          purchase?.updatedAt ||
          purchase?.createdAt ||
          null,
      };
      return acc;
    }, {});
  }, [purchaseLines, purchases]);

  const productOptions = useMemo(() => products
    .slice()
    .sort((a, b) => buildProductOptionLabel(a).localeCompare(buildProductOptionLabel(b), 'es')),
  [products]);

  const supplierOptions = useMemo(() => (suppliers || [])
    .slice()
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es')), [suppliers]);

  const normalizedSupplierName = String(form.supplierName || '').trim().toLowerCase();
  const selectedSupplier = useMemo(() => supplierOptions.find((supplier) => supplier.id === form.selectedSupplierId)
    || supplierOptions.find((supplier) => String(supplier.name || '').trim().toLowerCase() === normalizedSupplierName)
    || null, [supplierOptions, form.selectedSupplierId, normalizedSupplierName]);

  const selectedSupplierAddress = useMemo(() => (selectedSupplier?.addresses && selectedSupplier.addresses[0]) || null, [selectedSupplier]);

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [loadingAddressSuggestions, setLoadingAddressSuggestions] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressValidation, setAddressValidation] = useState(() => normalizeAddressValidationState());
  const addressAutocompleteRef = useRef(null);

  useEffect(() => {
    if (selectedSupplier) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setLoadingAddressSuggestions(false);
      setAddressValidation(normalizeAddressValidationState({
        status: selectedSupplierAddress?.latlng ? 'success' : 'idle',
        text: selectedSupplierAddress?.latlng
          ? 'Proveedor existente con ubicación ya validada.'
          : 'Proveedor existente sin coordenadas guardadas. Podés completarlas desde contactos o validar antes de guardar.',
        validatedAddress: buildSupplierValidatedAddressText({
          supplierAddress: selectedSupplierAddress?.address,
          supplierApartment: selectedSupplierAddress?.apartment,
          supplierLocality: selectedSupplierAddress?.locality,
          supplierPartido: selectedSupplierAddress?.partido,
          supplierProvince: selectedSupplierAddress?.province,
          supplierPostalCode: selectedSupplierAddress?.postalCode,
        }),
      }));
      return;
    }

    setAddressValidation((prev) => {
      if (!form.supplierLatlng) {
        return normalizeAddressValidationState({
          status: GEOAPIFY_API_KEY ? 'idle' : 'missing-key',
          validatedAddress: '',
        });
      }
      return normalizeAddressValidationState({
        status: 'success',
        text: 'Ubicación validada para este proveedor nuevo.',
        validatedAddress: buildSupplierValidatedAddressText(form),
      });
    });
  }, [
    selectedSupplier,
    selectedSupplierAddress,
    form.supplierLatlng,
    form.supplierAddress,
    form.supplierApartment,
    form.supplierLocality,
    form.supplierPartido,
    form.supplierProvince,
    form.supplierPostalCode,
  ]);

  useEffect(() => {
    if (selectedSupplier || !GEOAPIFY_API_KEY) return undefined;

    const queryText = toInputValue(form.supplierAddress);
    const shouldQuery = queryText.length >= 5 && (toInputValue(form.supplierLocality) || toInputValue(form.supplierPartido) || /\d/.test(queryText));

    if (!shouldQuery) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setLoadingAddressSuggestions(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        setLoadingAddressSuggestions(true);
        const url =
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(buildSupplierAutocompleteSearchText(form))}` +
          `&lang=es&limit=5&filter=countrycode:ar&apiKey=${GEOAPIFY_API_KEY}`;
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Geoapify autocomplete error: ${response.status}`);
        const data = await response.json();
        const results = Array.isArray(data?.features) ? data.features : [];
        setAddressSuggestions(results);
        setShowAddressSuggestions(true);
        setAddressValidation((prev) => prev.status === 'success' ? prev : normalizeAddressValidationState({
          status: results.length ? 'idle' : 'error',
          text: results.length
            ? 'Elegí una sugerencia o validá manualmente la dirección del proveedor.'
            : 'No encontré coincidencias claras. Completá mejor la dirección o validá manualmente.',
          validatedAddress: prev.validatedAddress,
        }));
      } catch (error) {
        if (error.name !== 'AbortError') {
          setAddressSuggestions([]);
          setShowAddressSuggestions(false);
          setAddressValidation((prev) => prev.status === 'success' ? prev : normalizeAddressValidationState({
            status: 'error',
            text: 'No pude cargar sugerencias de dirección. Probá validar manualmente.',
            validatedAddress: prev.validatedAddress,
          }));
        }
      } finally {
        setLoadingAddressSuggestions(false);
      }
    }, 350);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [
    selectedSupplier,
    form.supplierAddress,
    form.supplierLocality,
    form.supplierPartido,
    form.supplierProvince,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!addressAutocompleteRef.current?.contains(event.target)) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectAddressSuggestion = useCallback((feature) => {
    const patch = mapGeoapifySuggestionToPatch(feature);
    onSupplierAddressPatch(patch);
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setAddressValidation(normalizeAddressValidationState({
      status: 'success',
      text: 'Dirección sugerida seleccionada y aprobada para este proveedor.',
      validatedAddress: feature?.properties?.formatted || buildSupplierValidatedAddressText(patch),
    }));
  }, [onSupplierAddressPatch]);

  const handleValidateSupplierAddress = useCallback(async () => {
    const coords = await onGeocodeSupplier();
    if (coords) {
      setAddressValidation(normalizeAddressValidationState({
        status: 'success',
        text: 'Ubicación validada y lista para usarse en logística.',
        validatedAddress: buildSupplierValidatedAddressText(form),
      }));
      setShowAddressSuggestions(false);
    } else {
      setAddressValidation(normalizeAddressValidationState({
        status: 'error',
        text: 'No pude validar la ubicación. Revisá la dirección o elegí una sugerencia.',
        validatedAddress: '',
      }));
    }
  }, [onGeocodeSupplier, form]);

  const purchaseRows = useMemo(() => purchases.map((purchase) => {
    const lines = groupedLines[purchase.id] || [];
    const headerStatus = computePurchaseHeaderStatus(purchase, lines);
    const fallbackLineCount = Math.max(
      0,
      Number(purchase.pendingLinesCount || 0) + Number(purchase.completedLinesCount || 0)
    );
    const displayLineCount = lines.length > 0 ? lines.length : fallbackLineCount;
    const totalUnits = lines.reduce((acc, line) => acc + Number(line.orderedQuantity || 0), 0);
    const totalPending = lines.reduce((acc, line) => acc + Number(line.pendingPickupQuantity || 0), 0);
    const totalReceived = lines.reduce((acc, line) => acc + Number(line.receivedQuantity || 0), 0);
    const totalEstimated = lines.reduce((acc, line) => {
      const unitCost = Number(line.unitCost || 0);
      const ordered = Number(line.orderedQuantity || 0);
      return acc + (unitCost > 0 ? unitCost * ordered : 0);
    }, Number(purchase.totalEstimatedCost || 0));
    return {
      purchase,
      lines,
      headerStatus,
      displayLineCount,
      totalUnits,
      totalPending,
      totalReceived,
      totalEstimated,
    };
  }), [purchases, groupedLines]);

  const queueRows = useMemo(() => {
    const now = Date.now();
    const maxAgeMs = RECENTLY_CLOSED_DAYS * 24 * 60 * 60 * 1000;

    return purchaseRows.filter(({ purchase, headerStatus }) => {
      if (!CLOSED_PURCHASE_STATUSES.has(String(headerStatus || '').trim().toLowerCase())) {
        return true;
      }

      const closedAt = purchase.closedAt?.toDate?.() || (purchase.closedAt ? new Date(purchase.closedAt) : null);
      const updatedAt = purchase.updatedAt?.toDate?.() || (purchase.updatedAt ? new Date(purchase.updatedAt) : null);
      const referenceDate = closedAt || updatedAt;

      if (!referenceDate || Number.isNaN(referenceDate.getTime())) {
        return false;
      }

      return (now - referenceDate.getTime()) <= maxAgeMs;
    });
  }, [purchaseRows]);

  const summary = useMemo(() => ({
    total: queueRows.length,
    pending: queueRows.filter((item) => item.headerStatus === 'pending_pickup').length,
    inTransit: queueRows.filter((item) => item.headerStatus === 'in_transit' || item.headerStatus === 'assigned_to_route' || item.headerStatus === 'partially_assigned').length,
    received: queueRows.filter((item) => item.headerStatus === 'received' || item.headerStatus === 'partially_received').length,
  }), [queueRows]);

  const specialFlows = useMemo(() => products.reduce((acc, product) => {
    const buckets = product.stockBuckets || {};
    acc.repair += Number(buckets.repairQty || 0);
    acc.returned += Number(buckets.returnedQty || 0);
    acc.damaged += Number(buckets.damagedQty || 0);
    return acc;
  }, { repair: 0, returned: 0, damaged: 0 }), [products]);

  const updateLine = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line),
    }));
  };

  const updateQuickProduct = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, lineIndex) => lineIndex === index ? {
        ...line,
        quickProduct: {
          ...(line.quickProduct || QUICK_PRODUCT_TEMPLATE),
          ...patch,
        },
      } : line),
    }));
  };

  const addLine = () => {
    setForm((prev) => ({
      ...prev,
      lines: [...prev.lines, { productId: '', quantity: '', unitCost: '', quickProduct: null }],
    }));
  };

  const removeLine = (index) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, lineIndex) => lineIndex !== index),
    }));
  };

  const openQuickCreate = (index) => {
    updateLine(index, { quickProduct: form.lines[index]?.quickProduct || QUICK_PRODUCT_TEMPLATE });
  };

  const closeQuickCreate = (index) => {
    updateLine(index, { quickProduct: null });
  };

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600">Compras / pendientes</p>
            <h3 className="mt-1 text-xl font-bold text-slate-900">{editingPurchaseId ? 'Editar compra pendiente' : 'Ingreso esperado desde proveedor'}</h3>
            <p className="mt-1 max-w-3xl text-sm text-slate-500">
              {editingPurchaseId
                ? 'Podés ajustar esta compra mientras todavía no entró a logística. La idea es corregir proveedor, referencia o líneas sin romper la traza.'
                : 'Acá nace la mercadería nueva. Una compra no suma stock físico todavía: queda pendiente de retiro hasta que después la tome Logística o se confirme su ingreso real.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {editingPurchaseId ? (
              <button type="button" onClick={onCancelEdit} className={stockButtonTones.ghost}>
                Cancelar edición
              </button>
            ) : null}
            <div className="grid grid-cols-2 gap-2 text-center text-xs font-semibold text-slate-500 md:grid-cols-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2"><p>Compras</p><p className="text-lg text-slate-900">{summary.total}</p></div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2"><p>Pendientes</p><p className="text-lg text-slate-900">{summary.pending}</p></div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2"><p>En tránsito</p><p className="text-lg text-slate-900">{summary.inTransit}</p></div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2"><p>Recibidas</p><p className="text-lg text-slate-900">{summary.received}</p></div>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <p className="text-xs font-semibold uppercase tracking-[0.18em]">Devoluciones</p>
            <p className="mt-2 text-2xl font-bold text-amber-900">{specialFlows.returned}</p>
            <p className="mt-1 text-xs text-amber-700">Producto que volvió y espera definición o reingreso.</p>
          </div>
          <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-800">
            <p className="text-xs font-semibold uppercase tracking-[0.18em]">Reparación</p>
            <p className="mt-2 text-2xl font-bold text-violet-900">{specialFlows.repair}</p>
            <p className="mt-1 text-xs text-violet-700">Unidades derivadas a taller o fuera de disponibilidad.</p>
          </div>
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            <p className="text-xs font-semibold uppercase tracking-[0.18em]">Rotura</p>
            <p className="mt-2 text-2xl font-bold text-rose-900">{specialFlows.damaged}</p>
            <p className="mt-1 text-xs text-rose-700">Bajas por daño o mercadería que requiere revisión.</p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="space-y-1 md:col-span-2 xl:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Proveedor</span>
            <input
              list="stock-suppliers-list"
              value={form.supplierName}
              onChange={(e) => onSupplierNameChange(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              placeholder="Distribuidora Norte"
            />
            <datalist id="stock-suppliers-list">
              {supplierOptions.map((supplier) => (
                <option key={supplier.id} value={supplier.name} />
              ))}
            </datalist>
            {selectedSupplier ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                Proveedor existente detectado. {selectedSupplierAddress?.address ? `Se usará la ubicación guardada: ${[selectedSupplierAddress.address, selectedSupplierAddress.locality, selectedSupplierAddress.province].filter(Boolean).join(', ')}.` : 'No tiene dirección cargada todavía.'}
              </div>
            ) : form.supplierName.trim() ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                Proveedor nuevo. Completá la ubicación para geolocalizarlo y reutilizarlo después en Logística.
              </div>
            ) : null}
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fecha operativa</span>
            <input
              type="date"
              value={form.purchaseDate}
              onChange={(e) => setForm((prev) => ({ ...prev, purchaseDate: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Origen</span>
            <select
              value={form.sourceType}
              onChange={(e) => setForm((prev) => ({ ...prev, sourceType: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
            >
              <option value="manual">Manual</option>
              <option value="import_excel">Importación Excel</option>
              <option value="integration">Integración</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Referencia</span>
            <input
              value={form.sourceReference}
              onChange={(e) => setForm((prev) => ({ ...prev, sourceReference: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              placeholder="Factura, remito o nota interna"
            />
          </label>

          {!selectedSupplier ? (
            <>
              <label ref={addressAutocompleteRef} className="space-y-1 md:col-span-2 xl:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dirección del proveedor</span>
                <div className="relative">
                  <input
                    value={form.supplierAddress}
                    onChange={(e) => onSupplierAddressPatch({ supplierAddress: e.target.value })}
                    onFocus={() => {
                      if (addressSuggestions.length > 0) setShowAddressSuggestions(true);
                    }}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                    placeholder="Calle y número"
                  />
                  {loadingAddressSuggestions ? (
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                      <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4 animate-spin" />
                    </div>
                  ) : null}
                  {!selectedSupplier && showAddressSuggestions && addressSuggestions.length > 0 ? (
                    <div className="absolute z-20 mt-2 max-h-64 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                      {addressSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.properties?.place_id || suggestion.properties?.formatted}
                          type="button"
                          onClick={() => handleSelectAddressSuggestion(suggestion)}
                          className="w-full rounded-2xl px-3 py-2 text-left transition hover:bg-slate-50"
                        >
                          <p className="text-sm font-semibold text-slate-900">{suggestion.properties?.formatted || suggestion.properties?.address_line1 || 'Dirección sugerida'}</p>
                          <p className="mt-1 text-xs text-slate-500">{[suggestion.properties?.city || suggestion.properties?.town || suggestion.properties?.village, suggestion.properties?.state, suggestion.properties?.postcode].filter(Boolean).join(' · ')}</p>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Piso / Depto</span>
                <input
                  value={form.supplierApartment}
                  onChange={(e) => onSupplierAddressPatch({ supplierApartment: e.target.value })}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                  placeholder="Opcional"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Localidad</span>
                <input
                  value={form.supplierLocality}
                  onChange={(e) => onSupplierAddressPatch({ supplierLocality: e.target.value })}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                  placeholder="Localidad"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Partido</span>
                <input
                  value={form.supplierPartido}
                  onChange={(e) => onSupplierAddressPatch({ supplierPartido: e.target.value })}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                  placeholder="Partido"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Provincia</span>
                <select
                  value={form.supplierProvince}
                  onChange={(e) => onSupplierAddressPatch({ supplierProvince: e.target.value })}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                >
                  {ARGENTINA_PROVINCES.map((province) => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Código postal</span>
                <input
                  value={form.supplierPostalCode}
                  onChange={(e) => onSupplierAddressPatch({ supplierPostalCode: e.target.value })}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                  placeholder="Opcional"
                />
              </label>
              <div className="md:col-span-2 xl:col-span-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Validación de ubicación</p>
                    <p className="mt-1 text-sm text-slate-600">{addressValidation.text}</p>
                    {addressValidation.validatedAddress ? (
                      <p className="mt-1 text-xs text-emerald-700">Dirección aprobada: {addressValidation.validatedAddress}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={handleValidateSupplierAddress}
                    className="inline-flex items-center gap-2 rounded-2xl border border-cyan-200 bg-white px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-50"
                  >
                    <Icon path="M12 2.25c-4.97 0-9 3.806-9 8.5 0 6.396 8.196 10.517 8.545 10.69a1 1 0 00.91 0C12.804 21.267 21 17.146 21 10.75c0-4.694-4.03-8.5-9-8.5z" className="h-4 w-4" />
                    Validar ubicación en mapa
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${form.supplierLatlng ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500'}`}>
                    {form.supplierLatlng ? `Lat ${Number(form.supplierLatlng.lat || 0).toFixed(5)} · Lng ${Number(form.supplierLatlng.lng || 0).toFixed(5)}` : 'Todavía sin coordenadas validadas'}
                  </span>
                  {!GEOAPIFY_API_KEY ? (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold text-amber-700">
                      Sin clave de sugerencias automáticas
                    </span>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
          <label className="space-y-1 md:col-span-2 xl:col-span-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Observación</span>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              rows={2}
              className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              placeholder="Comentario breve, depósito esperado o instrucción operativa"
            />
          </label>
        </div>

        <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50/80 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Líneas de compra</p>
              <p className="mt-1 text-sm text-slate-500">Elegí productos del catálogo o crealos acá mismo. El costo queda opcional para dejar sembrada la lectura económica.</p>
            </div>
            <button
              onClick={addLine}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-cyan-200 hover:text-cyan-700"
            >
              <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
              Agregar línea
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {form.lines.map((line, index) => {
              const quickProduct = line.quickProduct || null;
              const selectedProduct = productOptions.find((product) => product.id === line.productId) || null;
              const latestPurchaseInsight = selectedProduct
                ? latestPurchaseInsightByProductId[String(selectedProduct.id || '')] || null
                : null;
              return (
                <div key={`purchase-line-${index}`} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="grid gap-3 md:grid-cols-[minmax(0,1.3fr)_130px_150px_auto] md:items-end">
                    <label className="space-y-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Producto</span>
                      <select
                        value={line.productId}
                        onChange={(e) => updateLine(index, { productId: e.target.value })}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                      >
                        <option value="">Seleccionar producto</option>
                        {productOptions.map((product) => (
                          <option key={product.id} value={product.id}>{buildProductOptionLabel(product)}</option>
                        ))}
                      </select>
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cantidad</span>
                      <input
                        type="number"
                        min="0"
                        value={line.quantity}
                        onChange={(e) => updateLine(index, { quantity: e.target.value })}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                        placeholder="10"
                      />
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Costo unitario</span>
                      <input
                        type="number"
                        min="0"
                        value={line.unitCost}
                        onChange={(e) => updateLine(index, { unitCost: e.target.value })}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
                        placeholder="Opcional"
                      />
                    </label>
                    <button
                      onClick={() => removeLine(index)}
                      className="inline-flex h-[50px] items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                    >
                      <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
                      Quitar
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {!quickProduct ? (
                      <button
                        onClick={() => openQuickCreate(index)}
                        className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-700 transition hover:bg-cyan-100"
                      >
                        <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                        Crear producto nuevo en esta línea
                      </button>
                    ) : (
                      <button
                        onClick={() => closeQuickCreate(index)}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
                      >
                        Cerrar alta rápida
                      </button>
                    )}
                  </div>

                  {selectedProduct ? (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Producto elegido: {selectedProduct.name}</p>
                          <p className="mt-1 text-xs text-slate-600">
                            {buildProductIdentityText(selectedProduct) === 'Sin identidad ampliada'
                              ? 'Esta ficha todavía no tiene modelo, marca o característica bien definidos.'
                              : buildProductIdentityText(selectedProduct)}
                          </p>
                        </div>
                        <div className="text-right text-xs text-slate-600">
                          <p className="font-semibold text-emerald-700">Stock actual: {getVisibleSystemStock(selectedProduct)}</p>
                          {selectedProduct.sku ? <p>SKU {selectedProduct.sku}</p> : null}
                        </div>
                      </div>
                      <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-xl border border-emerald-200 bg-white px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Producto principal</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{getProductFieldValue(selectedProduct.category, selectedProduct.name || 'Sin dato')}</p>
                        </div>
                        <div className="rounded-xl border border-emerald-200 bg-white px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Modelo</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{getProductFieldValue(selectedProduct.model, 'Sin modelo')}</p>
                        </div>
                        <div className="rounded-xl border border-emerald-200 bg-white px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Marca</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{getProductFieldValue(selectedProduct.brand, 'Sin marca')}</p>
                        </div>
                        <div className="rounded-xl border border-emerald-200 bg-white px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Característica</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{getProductFieldValue(selectedProduct.featureLabel, 'Sin característica')}</p>
                        </div>
                      </div>
                      <div className="mt-3 rounded-2xl border border-emerald-200 bg-white px-4 py-3">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Última compra</p>
                        {latestPurchaseInsight ? (
                          <div className="mt-2 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Proveedor</p>
                              <p className="mt-1 text-sm font-semibold text-slate-900">{latestPurchaseInsight.supplierName || 'Sin proveedor visible'}</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Costo unitario</p>
                              <p className="mt-1 text-sm font-semibold text-slate-900">{formatMoney(latestPurchaseInsight.unitCost)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Fecha</p>
                              <p className="mt-1 text-sm font-semibold text-slate-900">{formatDateTime(latestPurchaseInsight.dateValue)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Cantidad</p>
                              <p className="mt-1 text-sm font-semibold text-slate-900">{latestPurchaseInsight.orderedQuantity || 0} compradas</p>
                            </div>
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-slate-600">Todavía sin compras registradas para este producto.</p>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {quickProduct ? (
                    <div className="rounded-2xl border border-dashed border-cyan-200 bg-cyan-50/60 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Alta rápida de producto</p>
                          <p className="mt-1 text-xs text-slate-600">Creá la ficha catálogo sin salir de la compra. La cantidad de esta compra se completa arriba, en la línea.</p>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        <label className="space-y-1 xl:col-span-3">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nombre del producto</span>
                          <input
                            value={quickProduct.name}
                            onChange={(e) => updateQuickProduct(index, { name: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                            placeholder="Monitor 24 pulgadas"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Producto principal</span>
                          <input
                            value={quickProduct.category}
                            onChange={(e) => updateQuickProduct(index, { category: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                            placeholder="Teclado, monitor, cable..."
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Unidad base</span>
                          <input
                            value={quickProduct.unitBase}
                            onChange={(e) => updateQuickProduct(index, { unitBase: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                            placeholder="unidad"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Modelo</span>
                          <input
                            value={quickProduct.model}
                            onChange={(e) => updateQuickProduct(index, { model: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                            placeholder="K120, K380, Slim USB..."
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Marca</span>
                          <input
                            value={quickProduct.brand}
                            onChange={(e) => updateQuickProduct(index, { brand: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                            placeholder="Samsung, LG, Xtrike Me..."
                          />
                        </label>
                        <label className="space-y-1 xl:col-span-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Característica</span>
                          <input
                            value={quickProduct.featureLabel}
                            onChange={(e) => updateQuickProduct(index, { featureLabel: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                            placeholder="USB, mecánico, español, RGB..."
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Frecuencia de control</span>
                          <select
                            value={quickProduct.controlFrequency}
                            onChange={(e) => updateQuickProduct(index, { controlFrequency: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                          >
                            <option value="manual">Manual</option>
                            <option value="weekly">Semanal</option>
                            <option value="bi-weekly">Quincenal</option>
                            <option value="monthly">Mensual</option>
                          </select>
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">SKU</span>
                          <input
                            value={quickProduct.sku}
                            onChange={(e) => updateQuickProduct(index, { sku: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                            placeholder="MON24"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Código de barras</span>
                          <input
                            value={quickProduct.barcode}
                            onChange={(e) => updateQuickProduct(index, { barcode: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                            placeholder="Opcional"
                          />
                        </label>
                        {quickProduct.controlFrequency !== 'manual' ? (
                          <label className="space-y-1">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Día de control</span>
                            <select
                              value={quickProduct.controlDay}
                              onChange={(e) => updateQuickProduct(index, { controlDay: e.target.value })}
                              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400"
                            >
                              <option value="1">Lunes</option>
                              <option value="2">Martes</option>
                              <option value="3">Miércoles</option>
                              <option value="4">Jueves</option>
                              <option value="5">Viernes</option>
                              <option value="6">Sábado</option>
                              <option value="7">Domingo</option>
                            </select>
                          </label>
                        ) : null}
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          onClick={() => onQuickCreateProduct(index, quickProduct)}
                          className={stockButtonTones.primary}
                        >
                          <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                          Crear producto y usarlo en esta línea
                        </button>
                        <button
                          onClick={() => closeQuickCreate(index)}
                          className={stockButtonTones.ghost}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            El ingreso nace pendiente de retiro, puede crear proveedor nuevo con geolocalización y después pasar por logística, devolución, reparación o rotura.
          </div>
          <button
            onClick={onCreate}
            className={stockButtonTones.primary}
          >
            <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-5 w-5" />
            {editingPurchaseId ? 'Guardar cambios de la compra' : 'Registrar compra pendiente'}
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Cola operativa</p>
            <h4 className="mt-1 text-lg font-bold text-slate-900">Compras registradas</h4>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{queueRows.length} compras</span>
        </div>
        <p className="mt-2 text-sm text-slate-500">
          La cola muestra lo activo y deja visibles los cierres recientes durante {RECENTLY_CLOSED_DAYS} días. Después, la historia completa sigue en <span className="font-semibold text-slate-700">Movimientos</span>.
        </p>

        <div className="mt-4 space-y-3">
          {queueRows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              No hay compras activas ni cierres recientes en esta cola. Todo lo anterior queda trazado en Movimientos.
            </div>
          ) : queueRows.map(({ purchase, lines, headerStatus, displayLineCount, totalUnits, totalPending, totalReceived, totalEstimated }) => {
            const purchaseEditable = ['pending_pickup', 'draft'].includes(String(headerStatus || '').trim().toLowerCase())
              && lines.every((line) => computePurchaseLineStatus(line) === 'pending_pickup');
            const isRecentlyClosed = CLOSED_PURCHASE_STATUSES.has(String(headerStatus || '').trim().toLowerCase());
            return (
              <div key={purchase.id} className={`rounded-2xl border px-4 py-4 ${isRecentlyClosed ? 'border-slate-200 bg-slate-100/80 opacity-80' : 'border-slate-200 bg-slate-50'}`}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-base font-semibold text-slate-900">{purchase.supplierNameSnapshot || purchase.supplierName || 'Proveedor sin nombre'}</p>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${PURCHASE_STATUS_STYLES[headerStatus] || 'bg-slate-200 text-slate-600'}`}>
                        {PURCHASE_STATUS_LABELS[headerStatus] || 'Sin estado'}
                      </span>
                      <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                        {purchase.sourceType === 'manual' ? 'Manual' : purchase.sourceType === 'import_excel' ? 'Excel' : 'Integración'}
                      </span>
                      {isRecentlyClosed ? (
                        <span className="rounded-full border border-slate-300 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
                          Cierre reciente
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                      <span>{purchase.purchaseDate || 'Sin fecha'}</span>
                      <span>{displayLineCount} líneas</span>
                      <span>{totalUnits} unidades</span>
                      <span>{totalPending} pendientes</span>
                      <span>{totalReceived} ingresadas</span>
                      {totalEstimated > 0 ? <span>{formatMoney(totalEstimated, purchase.currency || 'ARS')}</span> : null}
                    </div>
                    {purchase.sourceReference ? <p className="mt-2 text-sm text-slate-600">Referencia: {purchase.sourceReference}</p> : null}
                    {purchase.note ? <p className="mt-2 text-sm text-slate-600">{purchase.note}</p> : null}
                  </div>
                  <div className="flex max-w-sm flex-col gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600 lg:items-end lg:text-right">
                    {purchaseEditable ? (
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onEditPurchase(purchase.id)}
                          className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-700 transition hover:bg-cyan-100"
                        >
                          <Icon path="M16.862 4.487l1.687-1.687a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897L16.862 4.487z" className="h-4 w-4" />
                          Editar compra
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeletePurchase(purchase.id)}
                          className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
                        >
                          <Icon path="M6 7.5h12m-9 0V6a1.5 1.5 0 011.5-1.5h3A1.5 1.5 0 0115 6v1.5m-7.5 0v9A1.5 1.5 0 009 18h6a1.5 1.5 0 001.5-1.5v-9" className="h-4 w-4" />
                          Eliminar compra
                        </button>
                      </div>
                    ) : (
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Solo lectura
                      </span>
                    )}
                    <p className="text-slate-500">
                      {purchaseEditable
                        ? 'Todavía no pasó a logística. Podés ajustar proveedor, referencia o líneas.'
                        : 'Si ya fue tomada por logística o recibió mercadería, la dejamos fija para cuidar la traza.'}
                    </p>
                  </div>
                </div>
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <div className="hidden bg-slate-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 md:grid md:grid-cols-[minmax(0,1.4fr)_140px_140px_140px_160px_140px_140px] md:gap-3">
                    <span>Producto / modelo</span>
                    <span>Proveedor</span>
                    <span>Cantidades</span>
                    <span>Estado</span>
                    <span>Costo proveedor</span>
                    <span>Impacto potencial</span>
                    <span>Trazabilidad</span>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {lines.length === 0 ? (
                      <div className="px-4 py-4 text-sm text-slate-500">
                        {displayLineCount > 0
                          ? 'Las líneas de esta compra todavía se están acomodando en la vista. La compra existe y su traza sigue viva en Movimientos.'
                          : 'Sin líneas registradas.'}
                      </div>
                    ) : lines.map((line) => {
                      const lineStatus = computePurchaseLineStatus(line);
                      const lineTotalCost = Number(line.unitCost || 0) > 0
                        ? Number(line.unitCost || 0) * Number(line.orderedQuantity || 0)
                        : 0;
                      const traceLabels = [];
                      if (line.lastRouteCodeSnapshot) traceLabels.push(`Ruta ${line.lastRouteCodeSnapshot}`);
                      if (line.lastMovementStatus) traceLabels.push(`Mov. ${line.lastMovementStatus}`);
                      if (!traceLabels.length && line.purchaseId) traceLabels.push(`Compra ${line.purchaseId.slice(0, 6)}`);
                      return (
                        <div key={line.id} className="grid gap-3 px-4 py-4 text-sm text-slate-600 md:grid-cols-[minmax(0,1.4fr)_140px_140px_140px_160px_140px_140px] md:items-start">
                          <div>
                            <p className="font-semibold text-slate-900">{line.productNameSnapshot || 'Producto sin nombre'}</p>
                            <p className="mt-1 text-xs text-slate-500">{line.skuSnapshot || 'Sin SKU'} · {line.categorySnapshot || 'Sin categoría'} · {line.unitSnapshot || 'unidad'}</p>
                            <p className="mt-2 text-xs text-slate-500"></p>
                          </div>
                          <div>
                            <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-400 md:hidden">Proveedor</span>
                            <p className="font-medium text-slate-700">{line.supplierNameSnapshot || purchase.supplierNameSnapshot || 'Proveedor'}</p>
                          </div>
                          <div>
                            <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-400 md:hidden">Cantidades</span>
                            <p><span className="font-semibold text-slate-900">{line.orderedQuantity || 0}</span> compradas</p>
                            <p className="text-xs text-slate-500">Pend. {line.pendingPickupQuantity || 0} · Ret. {line.pickedUpQuantity || 0} · Ing. {line.receivedQuantity || 0}</p>
                          </div>
                          <div>
                            <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-400 md:hidden">Estado</span>
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${PURCHASE_LINE_STATUS_STYLES[lineStatus] || 'bg-slate-200 text-slate-600'}`}>
                              {PURCHASE_LINE_STATUS_LABELS[lineStatus] || 'Sin estado'}
                            </span>
                          </div>
                          <div>
                            <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-400 md:hidden">Costo proveedor</span>
                            <p className="font-semibold text-slate-900">{Number(line.unitCost || 0) > 0 ? formatMoney(line.unitCost, purchase.currency || 'ARS') : 'Manual pendiente'}</p>
                            <p className="text-xs text-slate-500">{lineTotalCost > 0 ? `Total ${formatMoney(lineTotalCost, purchase.currency || 'ARS')}` : 'Todavía sin total calculado'}</p>
                          </div>
                          <div>
                            <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-400 md:hidden">Impacto</span>
                            <p className="font-medium text-slate-700">{lineStatus === 'stored' || lineStatus === 'received' ? 'Impacta físico' : lineStatus === 'inbound_in_transit' || lineStatus === 'in_transit' ? 'En tránsito de entrada' : 'Todavía no impacta físico'}</p>
                            <p className="text-xs text-slate-500"></p>
                          </div>
                          <div>
                            <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-400 md:hidden">Trazabilidad</span>
                            <p className="text-xs text-slate-600">{traceLabels.join(' · ')}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StockPurchasesPanel;
